import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core'
import { Button, Icon, Loader } from 'foundations-library/components'
import RenderEmptyState, { EmptyStateScreen } from 'empty-states'
import { NavigationContext } from 'navigation/NavigationContext'
import { useSmsRegistrationContext } from '../../contexts/SmsRegistrationContext'
import WatchDemoDialog from '../../../../sms-enrollment/src/components/WatchDemoDialog'
import { getValue } from 'remote-config-value'
import PhoneComUser from 'phone-com-user'

const styles = (theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minHeight: 0,
        width: '100%'
    },
    conversationCustom: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 6,
        width: '100%',
        minHeight: 36,
        padding: '6px 10px',
        boxSizing: 'border-box',
        background: 'linear-gradient(78.05deg, #AADBA0 9.2%, #D7EBD3 48.86%, #AADBA0 91.7%)',
        borderRadius: 5
    },
    conversationCustomIcon: {
        display: 'flex'
    },
    conversationCustomText: {
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: 13,
        lineHeight: '21px',
        letterSpacing: 0
    },
    learnMoreLink: {
        color: theme['foundations-library'].colors.primary[500],
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:visited': {
            textDecoration: 'underline',
            color: theme['foundations-library'].colors.primary[500]
        }
    },
    numberSetupDescription: {
        width: '100%',
        maxWidth: 528,
        margin: '0 auto',
        fontFamily: 'Inter, sans-serif',
        fontSize: 15,
        fontWeight: 400,
        lineHeight: '23px',
        letterSpacing: '-0.1px',
        textAlign: 'center'
    },
    numberSetupAction: {
        color: theme['foundations-library'].colors.primary[500],
        fontWeight: 600
    }
})

const CustomContentView = ({ classes }) => (
    <div className={classes.conversationCustom}>
        <span className={classes.conversationCustomIcon} aria-hidden='true'>
            <Icon name='infotip-sm' size={24} color='black' />
        </span>
        <span className={classes.conversationCustomText}>
            Contact your admin to get started with texting.
        </span>
    </div>
)

CustomContentView.propTypes = {
    classes: PropTypes.object.isRequired
}

const getSMSActivationPromptContent = ({
    classes,
    onWatchDemoClick,
    onStartRegistrationClick
}) => ({
    title: 'Activate Business SMS',
    description:
        'To send and receive texts under standard carrier guidelines, verify your business profile in a few simple steps. This one-time setup protects your messaging reputation and ensures your texts bypass spam filters.',
    customContent: (
        <div className={classes.numberSetupDescription}>
            Toll-Free numbers are exempt. <a
                href={getValue('sms_registration_brands_learn_more_link')}
                target="_blank"
                className={classes.learnMoreLink}
                data-testid='landing-page-learn-more' rel="noreferrer"
            >Learn more
            </a>
        </div>
    ),
    buttons: [
        <Button
            key='watch-demo'
            variant='outlined'
            color='neutral'
            iconLeft
            icon='play-outline'
            size='large'
            onClick={onWatchDemoClick}
        >
            Watch a demo video
        </Button>,
        <Button
            key='start-registration'
            variant='contained'
            color='primary'
            iconLeft
            icon='add'
            size='large'
            onClick={onStartRegistrationClick}
        >
            Start SMS Registration
        </Button>
    ]
})

const getSMSBlockedContent = (classes) => ({
    title: "Texts aren't enabled for your account",
    description: '',
    customContent: <CustomContentView classes={classes} />,
    buttons: [
        <Button
            key='start-chat'
            variant='contained'
            color='neutral'
            disabled
            size='large'
            icon='go'
        >
            Let’s start a new chat
        </Button>
    ]
})

const getStartConversationContent = (onStartConversationClick) => ({
    title: 'Start a new conversation',
    description: 'Type your first message below',
    buttons: [
        <Button
            key='start-chat'
            variant='contained'
            color='primary'
            icon='go'
            size='large'
            onClick={onStartConversationClick}
        >
            Let’s start a new chat
        </Button>
    ]
})

const getNumberSetupContent = (classes) => ({
    title: 'Let\'s get your text number set up.',
    description: '',
    customContent: (
        <div className={classes.numberSetupDescription}>
            Once connected, you&apos;ll be able to send and receive texts right from this extension. <a className={classes.learnMoreLink} href={getValue('sms_registration_brands_learn_more_link')}>Assign a phone number to this extension</a>
        </div>
    ),
    buttons: []
})

const EmptyState = ({ content, smallView }) => (
    <RenderEmptyState
        title={{ text: content.title }}
        description={{ text: content.description }}
        svg={{ name: smallView ? 'message' : 'messages-content' }}
        smallView={smallView}
        customContent={content.customContent}
        buttons={content.buttons}
    />
)

EmptyState.propTypes = {
    content: PropTypes.object.isRequired,
    smallView: PropTypes.bool
}

const NoConversationUserView = ({ classes, onStartConversationClick, isSMSRegistrationCompleted, smallView }) => {
    const content = isSMSRegistrationCompleted
        ? getStartConversationContent(onStartConversationClick)
        : getSMSBlockedContent(classes)

    return <EmptyState content={content} smallView={smallView} />
}

NoConversationUserView.propTypes = {
    isSMSRegistrationCompleted: PropTypes.bool.isRequired,
    classes: PropTypes.object.isRequired,
    onStartConversationClick: PropTypes.func.isRequired,
    smallView: PropTypes.bool
}

const NoConversationAdminView = ({
    classes,
    onStartConversationClick,
    onStartRegistrationClick,
    smallView
}) => {
    const [showDemoModal, setShowDemoModal] = useState(false)

    const {
        attachedNumber,
        isSMSRegistrationCompleted,
        loadingSmsNumberCampaignContext = false
    } = useSmsRegistrationContext()

    const navigationContext = useContext(NavigationContext)

    if (loadingSmsNumberCampaignContext) {
        return (<div className={classes.root}><Loader size={40} data-testid='dialog-loader' /></div>)
    }

    const content = isSMSRegistrationCompleted
        ? attachedNumber
            ? getStartConversationContent(onStartConversationClick)
            : getNumberSetupContent(classes)
        : getSMSActivationPromptContent({
            classes,
            onWatchDemoClick: () => setShowDemoModal(true),
            onStartRegistrationClick: () => {
                const path = navigationContext?.getPathToNavItem('sms-registration')
                if (path) {
                    onStartRegistrationClick(path)
                }
            }
        })

    return (
        <>
            <EmptyState content={content} smallView={smallView} />
            <WatchDemoDialog
                isOpen={showDemoModal}
                onClose={() => setShowDemoModal(false)}
            />
        </>
    )
}

NoConversationAdminView.propTypes = {
    classes: PropTypes.object.isRequired,
    onStartConversationClick: PropTypes.func.isRequired,
    onStartRegistrationClick: PropTypes.func.isRequired,
    smallView: PropTypes.bool
}

const NoConversationView = ({
    extension,
    classes,
    onStartConversationClick,
    onStartRegistrationClick,
    onBack,
    smallView
}) => {
    const isAdmin = PhoneComUser.getRole() === 'account'
    const hasRegisteredNumbers = Boolean(Object.keys(extension?.phone_number || {}).length)
    const isSMSRegistrationCompleted = hasRegisteredNumbers

    const content = isAdmin
        ? (
            <NoConversationAdminView
                classes={classes}
                onStartConversationClick={onStartConversationClick}
                onStartRegistrationClick={onStartRegistrationClick}
                smallView={smallView}
            />
        )
        : (
            <NoConversationUserView
                isSMSRegistrationCompleted={isSMSRegistrationCompleted}
                classes={classes}
                onStartConversationClick={onStartConversationClick}
                smallView={smallView}
            />
        )

    return (
        <EmptyStateScreen headerTitle="Texts" onBack={onBack} smallView={smallView}>
            {content}
        </EmptyStateScreen>
    )
}

NoConversationView.propTypes = {
    classes: PropTypes.object.isRequired,
    onStartConversationClick: PropTypes.func.isRequired,
    onStartRegistrationClick: PropTypes.func.isRequired,
    extension: PropTypes.object.isRequired,
    onBack: PropTypes.func.isRequired,
    smallView: PropTypes.bool
}

export default withStyles(styles)(NoConversationView)
